import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuthUser } from "@/hooks/useAuthUser";
import { Navigation } from "./dashboard/Navigation";
import { UserMenu } from "./UserMenu";
import { AuthActionButtons } from "@/components/auth/AuthActionButtons";

interface PublicLayoutProps {
  children: React.ReactNode;
}

export default function PublicLayout({ children }: PublicLayoutProps) {
  const { profile, isSignedIn, isLoaded, loading } = useAuthUser();
  const currentUser = profile; // For compatibility
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Link to="/" className="text-xl font-bold text-gray-800 flex items-center gap-2">
              <img 
                src="https://v1.calone.com/static/graphics/CalOne_31_Logo.png" 
                alt="CalOne Logo" 
                className="h-8 w-8"
              />
              <span>
                <span className="text-[#669933]">CalOne</span>
                <span>
                  {" "}
                  <span className="text-[#4285F4]">E</span>
                  <span className="text-[#DB4437]">v</span>
                  <span className="text-[#F4B400]">e</span>
                  <span className="text-[#0F9D58]">n</span>
                  <span className="text-[#4285F4]">t</span>
                  <span className="text-[#9C27B0]">s</span>
                </span>
              </span>
            </Link>
            
            <div className="flex items-center gap-4">
              {!isLoaded || loading ? (
                /* Show nothing while authentication is loading to prevent flash */
                <div className="w-10 h-10" /> /* Placeholder to maintain layout */
              ) : currentUser ? (
                <>
                  {/* Only hamburger menu for signed in users */}
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={toggleMobileMenu}
                    className="text-gray-700"
                    aria-label="Toggle menu"
                  >
                    {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                  </Button>
                </>
              ) : (
                <div className="flex items-center gap-2">
                  <AuthActionButtons />
                </div>
              )}
            </div>
          </div>
          
          {/* Mobile menu */}
          {currentUser && mobileMenuOpen && (
            <nav className="py-2 border-t border-gray-200 bg-white shadow-lg">
              <Navigation closeMenu={closeMobileMenu} />
              <div className="pt-2 mt-2 border-t border-gray-200">
                <div className="px-2 py-3 flex justify-start">
                  <UserMenu />
                </div>
              </div>
            </nav>
          )}
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1">
        {children}
      </main>
    </div>
  );
}
